'use client'

import { SearchIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { selectionForInstrument } from '@/features/order/order-form/lib/linked-instrument'
import { useOrderFormDispatch } from '@/hooks/use-order-form-dispatch'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { resolveExchangeTradeability } from '@/lib/utils/account-select'
import { useUserAccounts } from '@/service/account.hook'
import type { Exchange, Instrument } from '@/types/generated/common/types'

const WatchlistWizard = dynamic(
  () =>
    import('@/features/dashboard/components/watchlist-wizard/watchlist-wizard').then(
      (module) => module.WatchlistWizard
    ),
  { ssr: false }
)

interface OrderFormEmptyStateProps {
  cardId: string
  className?: string
}

/**
 * The blank-ticket state of the Order Form. Instead of pointing the user at another widget
 * ("select a symbol from the watchlist"), it carries its own instrument picker.
 */
export function OrderFormEmptyState({ cardId, className }: OrderFormEmptyStateProps) {
  const t = useTranslations('orderForm')
  const [pickerOpen, setPickerOpen] = useState(false)
  const { setOrderFormSelection } = useOrderFormDispatch(cardId)
  const { data: userAccountsData } = useUserAccounts()
  const accounts = userAccountsData?.accounts

  const untradeableMessage = (exchange: Exchange) => {
    const tradeability = resolveExchangeTradeability(exchange, accounts ?? [])
    if (tradeability.tradeable) return null
    return tradeability.reason === 'no-trade-venue'
      ? t('venueViewOnly', { venue: tradeability.label })
      : t('connectVenueToTrade', { venue: tradeability.label })
  }

  const handleSelect = (instrument: Instrument) => {
    const message = untradeableMessage(instrument.exchange)
    if (message) {
      toast.error(message)
      return
    }
    setOrderFormSelection(selectionForInstrument(instrument))
  }

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center',
        className
      )}
    >
      <div className="flex flex-col gap-1.5">
        <h3 className="text-foreground/80 text-sm font-semibold">{t('startYourOrder')}</h3>
        <p className="text-foreground/50 text-xs leading-relaxed">{t('pickInstrumentHint')}</p>
      </div>
      <Button variant="cta" size="sm" onClick={() => setPickerOpen(true)}>
        <SearchIcon className="size-4" />
        {t('pickInstrument')}
      </Button>
      {pickerOpen && (
        <WatchlistWizard
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          mode="single"
          onSelect={handleSelect}
        />
      )}
    </div>
  )
}
